import {
  formatCampaignDate,
  getPercentage,
  smsCounts,
  smsRecipientCount,
} from '../utils/campaignDashboardUtils';
export const EMAIL_LIST_CONFIG = {
  channel: 'email',
  listUrl: '/api/campaigns',
  deleteUrl: '/api/campaigns',
  refreshIntervalMs: 30000,
  exportFileName: 'Campaigns.xlsx',
  title: 'Campaigns',
  subtitle:
    'Campaign status and analytics refresh automatically while emails are being delivered.',
  deleteItemLabel: 'campaign',
  emptyChannel: 'email',
  initialFilters: {
    campaignName: '',
    sentDate: '',
    recipients: '',
    opens: '',
    clicks: '',
    unsubscribed: '',
    status: '',
  },
  columns: [
    { id: 'campaignName', label: 'Campaign Name', filterKey: 'campaignName' },
    { id: 'sentDate', label: 'Date Sent', filterKey: 'sentDate' },
    { id: 'recipients', label: 'Recipients', filterKey: 'recipients' },
    { id: 'opens', label: 'Opens', filterKey: 'opens', withPercent: 'recipients' },
    { id: 'clicks', label: 'Clicks', filterKey: 'clicks', withPercent: 'recipients' },
    {
      id: 'unsubscribed',
      label: 'Unsubscribed',
      filterKey: 'unsubscribed',
      withPercent: 'recipients',
    },
    { id: 'status', label: 'Status', filterKey: 'status', isStatus: true },
  ],
  visualStats: [
    { id: 'recipients', label: 'Recipients', fixedPercent: '100%' },
    { id: 'opens', label: 'Opens', withPercent: 'recipients' },
    { id: 'clicks', label: 'Clicks', withPercent: 'recipients' },
    { id: 'unsubscribed', label: 'Unsubscribed', withPercent: 'recipients' },
  ],
  getDateLabel: (campaign) => {
    if (campaign.status === 'draft') {
      return formatCampaignDate(campaign.updatedAt) || 'Draft';
    }
    return formatCampaignDate(campaign.sentDate) || 'N/A';
  },
  getSubtitle: () => null,
  matchesFilters(campaign, filters) {
    const status =
      campaign.status === 'draft' ? 'draft' : campaign.status || 'pending';
    return (
      campaign.campaignName?.toLowerCase().includes(filters.campaignName.toLowerCase()) &&
      (campaign.status === 'draft'
        ? formatCampaignDate(campaign.updatedAt)
        : formatCampaignDate(campaign.sentDate)
      )
        .toLowerCase()
        .includes(filters.sentDate.toLowerCase()) &&
      String(campaign.recipients || 0).includes(filters.recipients) &&
      String(campaign.opens || 0).includes(filters.opens) &&
      String(campaign.clicks || 0).includes(filters.clicks) &&
      String(campaign.unsubscribed || 0).includes(filters.unsubscribed) &&
      status.toLowerCase().includes(filters.status.toLowerCase())
    );
  },
  getStatus(campaign) {
    if (campaign.status === 'draft') return 'draft';
    return campaign.status || 'pending';
  },
  getCellValue(campaign, columnId) {
    switch (columnId) {
      case 'campaignName':
        return campaign.campaignName || 'No Name';
      case 'sentDate':
        if (campaign.status === 'draft') {
          return formatCampaignDate(campaign.updatedAt) || 'Draft';
        }
        return formatCampaignDate(campaign.sentDate) || 'N/A';
      case 'recipients':
        return campaign.recipients || 0;
      case 'opens':
        return campaign.opens || 0;
      case 'clicks':
        return campaign.clicks || 0;
      case 'unsubscribed':
        return campaign.unsubscribed || 0;
      default:
        return '';
    }
  },
  getPercentTotal(campaign, columnId) {
    if (columnId === 'opens' || columnId === 'clicks' || columnId === 'unsubscribed') {
      return campaign.recipients || 0;
    }
    return 0;
  },
  getVisualStatValue(campaign, statId) {
    if (statId === 'recipients') return campaign.recipients || 0;
    if (statId === 'opens') return campaign.opens || 0;
    if (statId === 'clicks') return campaign.clicks || 0;
    if (statId === 'unsubscribed') return campaign.unsubscribed || 0;
    return 0;
  },
  getVisualStatPercent(campaign, stat) {
    if (stat.fixedPercent) return stat.fixedPercent;
    const total = campaign.recipients || 0;
    const value = EMAIL_LIST_CONFIG.getVisualStatValue(campaign, stat.id);
    return getPercentage(value, total);
  },
  normalizeCampaign(campaign) {
    return { ...campaign, unsubscribed: campaign.unsubscribed || 0 };
  },
};

export const SMS_LIST_CONFIG = {
  channel: 'sms',
  listUrl: '/api/sms-campaigns',
  deleteUrl: '/api/sms-campaigns',
  refreshIntervalMs: 10000,
  exportFileName: 'SMS_Campaigns.xlsx',
  title: 'Campaigns',
  subtitle:
    'Analytics refresh automatically as delivery reports arrive from your SMS provider.',
  deleteItemLabel: 'SMS campaign',
  emptyChannel: 'sms',
  initialFilters: {
    campaignName: '',
    createdAt: '',
    recipients: '',
    sent: '',
    delivered: '',
    failed: '',
    status: '',
  },
  columns: [
    { id: 'campaignName', label: 'Campaign Name', filterKey: 'campaignName' },
    { id: 'createdAt', label: 'Date Created', filterKey: 'createdAt' },
    { id: 'recipients', label: 'Recipients', filterKey: 'recipients' },
    { id: 'sent', label: 'Sent', filterKey: 'sent', withPercent: 'recipients' },
    {
      id: 'delivered',
      label: 'Delivered',
      filterKey: 'delivered',
      withPercent: 'recipients',
    },
    { id: 'failed', label: 'Failed', filterKey: 'failed', withPercent: 'recipients' },
    { id: 'status', label: 'Status', filterKey: 'status', isStatus: true },
  ],
  visualStats: [
    { id: 'recipients', label: 'Recipients', fixedPercent: '100%' },
    { id: 'sent', label: 'Sent', withPercent: 'recipients' },
    { id: 'delivered', label: 'Delivered', withPercent: 'recipients' },
    { id: 'failed', label: 'Failed', withPercent: 'recipients' },
  ],
  getDateLabel: (campaign) => {
    if (campaign.status === 'draft') {
      return formatCampaignDate(campaign.updatedAt) || 'Draft';
    }
    return formatCampaignDate(campaign.createdAt) || 'N/A';
  },
  getSubtitle: (campaign) =>
    campaign.status === 'draft'
      ? 'Draft'
      : `Sender: ${campaign.senderPhone || campaign.draftSenderPhone || '—'}`,
  matchesFilters(campaign, filters) {
    const total =
      campaign.status === 'draft'
        ? campaign.draftRecipientSource === 'paste'
          ? (campaign.draftManualContacts || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean).length
          : (campaign.draftRecipientGroupNames || []).length
        : smsRecipientCount(campaign);
    const counts = smsCounts(campaign);
    return (
      campaign.campaignName?.toLowerCase().includes(filters.campaignName.toLowerCase()) &&
      (campaign.status === 'draft'
        ? formatCampaignDate(campaign.updatedAt)
        : formatCampaignDate(campaign.createdAt)
      )
        .toLowerCase()
        .includes(filters.createdAt.toLowerCase()) &&
      String(total).includes(filters.recipients) &&
      String(counts.sent).includes(filters.sent) &&
      String(counts.delivered).includes(filters.delivered) &&
      String(counts.failed).includes(filters.failed) &&
      (campaign.status || '').toLowerCase().includes(filters.status.toLowerCase())
    );
  },
  getStatus(campaign) {
    if (campaign.status === 'draft') return 'draft';
    return campaign.status || 'pending';
  },
  getCellValue(campaign, columnId) {
    const total = smsRecipientCount(campaign);
    const counts = smsCounts(campaign);
    switch (columnId) {
      case 'campaignName':
        return campaign.campaignName || 'No Name';
      case 'createdAt':
        if (campaign.status === 'draft') {
          return formatCampaignDate(campaign.updatedAt) || 'Draft';
        }
        return formatCampaignDate(campaign.createdAt) || 'N/A';
      case 'recipients':
        if (campaign.status === 'draft') {
          if (campaign.draftRecipientSource === 'paste') {
            return (campaign.draftManualContacts || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean).length;
          }
          return (campaign.draftRecipientGroupNames || []).length;
        }
        return total;
      case 'sent':
        return counts.sent;
      case 'delivered':
        return counts.delivered;
      case 'failed':
        return counts.failed;
      default:
        return '';
    }
  },
  getPercentTotal(campaign) {
    return smsRecipientCount(campaign);
  },
  getVisualStatValue(campaign, statId) {
    const counts = smsCounts(campaign);
    if (statId === 'recipients') return smsRecipientCount(campaign);
    if (statId === 'sent') return counts.sent;
    if (statId === 'delivered') return counts.delivered;
    if (statId === 'failed') return counts.failed;
    return 0;
  },
  getVisualStatPercent(campaign, stat) {
    if (stat.fixedPercent) return stat.fixedPercent;
    const total = smsRecipientCount(campaign);
    const value = SMS_LIST_CONFIG.getVisualStatValue(campaign, stat.id);
    return getPercentage(value, total);
  },
  normalizeCampaign(campaign) {
    return campaign;
  },
};

export function getCampaignListConfig(channel) {
  return channel === 'sms' ? SMS_LIST_CONFIG : EMAIL_LIST_CONFIG;
}
